import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout, TextInput, Button, Alert } from '../components';
import { trackEvent } from '../services/analytics';

export const RegisterPage: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordsDoNotMatch', { ns: 'validation' }));
      return;
    }

    setIsLoading(true);

    try {
      await register(
        formData.email,
        formData.password,
        formData.firstName,
        formData.lastName,
        formData.phone
      );
      trackEvent('sign_up');
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.registrationFailed', { ns: 'validation' }));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('register.title', { ns: 'auth' })}
      subtitle={t('register.subtitle', { ns: 'auth' })}
    >
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error && <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />}

        <TextInput
          id="firstName"
          name="firstName"
          label={t('register.firstNameLabel', { ns: 'auth' })}
          placeholder={t('register.firstNamePlaceholder', { ns: 'auth' })}
          value={formData.firstName}
          onChange={handleChange}
          required
        />

        <TextInput
          id="lastName"
          name="lastName"
          label={t('register.lastNameLabel', { ns: 'auth' })}
          placeholder={t('register.lastNamePlaceholder', { ns: 'auth' })}
          value={formData.lastName}
          onChange={handleChange}
          required
        />

        <TextInput
          id="email"
          name="email"
          label={t('register.emailLabel', { ns: 'auth' })}
          type="email"
          placeholder={t('register.emailPlaceholder', { ns: 'auth' })}
          value={formData.email}
          onChange={handleChange}
          required
          autoComplete="email"
        />

        <TextInput
          id="phone"
          name="phone"
          label={t('register.phoneLabel', { ns: 'auth' })}
          type="tel"
          placeholder={t('register.phonePlaceholder', { ns: 'auth' })}
          value={formData.phone}
          onChange={handleChange}
          helpText={t('register.phoneHelpText', { ns: 'auth' })}
        />

        <TextInput
          id="password"
          name="password"
          label={t('register.passwordLabel', { ns: 'auth' })}
          type="password"
          placeholder={t('register.passwordPlaceholder', { ns: 'auth' })}
          value={formData.password}
          onChange={handleChange}
          required
          autoComplete="new-password"
        />

        <TextInput
          id="confirmPassword"
          name="confirmPassword"
          label={t('register.confirmPasswordLabel', { ns: 'auth' })}
          type="password"
          placeholder={t('register.confirmPasswordPlaceholder', { ns: 'auth' })}
          value={formData.confirmPassword}
          onChange={handleChange}
          required
          autoComplete="new-password"
        />

        <Button type="submit" variant="primary" size="md" fullWidth isLoading={isLoading}>
          {isLoading ? t('register.submitLoading', { ns: 'auth' }) : t('register.submitIdle', { ns: 'auth' })}
        </Button>

        <div className="text-center">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            {t('register.alreadyHaveAccount', { ns: 'auth' })}{' '}
            <Link to="/login" className="font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400">
              {t('register.signInLink', { ns: 'auth' })}
            </Link>
          </p>
        </div>
      </form>
    </AuthLayout>
  );
};
